import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { useConfirm } from "material-ui-confirm";
import { get, post, handleResponse } from "../../api";
import Breadcrumb from "../../components/Breadcrumb";
import Button from "../../components/Button";
import Frame from "../../components/Frame";
import FrameHeader from "../../components/FrameHeader";
import LoadingFrame from "../../components/LoadingFrame";
import SingleColumnPage from "../../components/SingleColumnPage";
import Space from "../../components/Space";
import styles from "./SignupApproval.module.scss";

const fullName = (a) =>
  [a.first_name, a.last_name].filter(Boolean).join(" ") || a.email;

/**
 * Where the Approve button in the "sign-up attempt" email lands. Requires a
 * logged-in administrator of the customer: the token in the URL only says
 * which attempt, the session says who approves. A second approval, by
 * anyone, shows who already did it instead.
 */
export default function SignupApproval({ match: { params } }) {
  const confirm = useConfirm();
  const [attempt, setAttempt] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [justApproved, setJustApproved] = useState(false);

  const url = `/customers/domain/registration-attempt/${params.token}`;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      // Raw response: a bad link is a message on this page, not the error page.
      const res = await get(url, {}, false);
      if (cancelled) return;
      if (res && res.ok) {
        setAttempt(await res.json());
      } else if (res && (res.status === 404 || res.status === 403)) {
        const body = await res.json().catch(() => null);
        setError(
          (body && body.responseStatus && body.responseStatus.message) ||
            "This approval link is not valid."
        );
      } else if (res) {
        await handleResponse(res);
      } else {
        setError("Sorry, unable to load this request. Try again.");
      }
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [url]);

  function approve() {
    confirm({
      description: `Add ${fullName(attempt)} (${attempt.email}) to ${attempt.customer_name} as a regular user? They will be emailed an invitation.`,
      title: null,
      dialogProps: { fullScreen: false },
      cancellationButtonProps: { color: "error", disableRipple: true },
      confirmationButtonProps: { color: "primary", disableRipple: true },
    })
      .then(async () => {
        setApproving(true);
        const res = await post(`${url}/approve`, {}, false);
        if (res && res.ok) {
          setAttempt(await res.json());
          setJustApproved(true);
        } else if (res && res.status === 409) {
          /**
           * Someone else got there first, or the link expired meanwhile.
           * Show the current state rather than the stale button.
           */
          const body = await res.json().catch(() => null);
          const current = await get(url);
          if (current) setAttempt(current);
          if (
            !current ||
            current.status !== "approved" ||
            !(body && body.responseStatus)
          ) {
            setError(
              (body && body.responseStatus && body.responseStatus.message) ||
                "This request could not be approved."
            );
          }
        } else if (res) {
          await handleResponse(res);
        }
        setApproving(false);
      })
      .catch(() => {});
  }

  function breadcrumbs() {
    return <Breadcrumb title="Sign-up approval" />;
  }

  function renderDetails() {
    return (
      <dl className={styles.details}>
        <dt className={styles.term}>Name</dt>
        <dd className={styles.value}>{fullName(attempt)}</dd>
        <dt className={styles.term}>Email</dt>
        <dd className={styles.value}>{attempt.email}</dd>
        {attempt.company && (
          <>
            <dt className={styles.term}>Company</dt>
            <dd className={styles.value}>{attempt.company}</dd>
          </>
        )}
        {attempt.phone && (
          <>
            <dt className={styles.term}>Phone</dt>
            <dd className={styles.value}>{attempt.phone}</dd>
          </>
        )}
        <dt className={styles.term}>Tried to sign up</dt>
        <dd className={styles.value}>
          {moment(attempt.created_time).format("MMMM Do YYYY [at] h:mma")}
        </dd>
      </dl>
    );
  }

  function renderStatus() {
    switch (attempt.status) {
      case "approved":
        return (
          <p className={styles.notice}>
            {justApproved
              ? `${fullName(attempt)} has been added to ${attempt.customer_name} as a regular user and emailed an invitation.`
              : `${attempt.approved_by || "An administrator"} already approved this request on ${moment(attempt.approved_time).format("MMMM Do YYYY [at] h:mma")}.`}
          </p>
        );
      case "expired":
        return (
          <p className={styles.notice}>
            This approval link has expired. Ask the person to sign up again,
            or add them on the Users page.
          </p>
        );
      default:
        return (
          <p className={styles.notice}>
            Approving adds them to {attempt.customer_name} as a regular user
            (no administrator rights) and emails them an invitation. If you
            do not recognize them, do nothing.
          </p>
        );
    }
  }

  return (
    <SingleColumnPage breadcrumbs={breadcrumbs}>
      <FrameHeader title="Sign-up approval" />
      {loading ? (
        <LoadingFrame />
      ) : (
        <Frame>
          {error ? (
            <p className={styles.notice}>{error}</p>
          ) : (
            <>
              <Space size="s">{renderDetails()}</Space>
              {renderStatus()}
              <div className={styles.actions}>
                {attempt.status === "pending" && (
                  <Button
                    theme="primary"
                    isLoading={approving}
                    disabled={approving}
                    onClick={approve}
                  >
                    Approve
                  </Button>
                )}
                <Link to="/customer/users">
                  <Button>Users</Button>
                </Link>
              </div>
            </>
          )}
        </Frame>
      )}
    </SingleColumnPage>
  );
}
